import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Logger } from 'utils/Logger';
import { reelMakerApi } from '../ReelMaker.api';
import { Reel, ReelExportQueued, ReelVersionExportStatus, ReelVersionSnapshot, ReelVersionsResponse } from '../ReelMaker.types';
import { ReelPlayer } from '../ReelPlayer';

const LOG_PREFIX = 'components/ReelMaker/dialogs/ReelExportDialog.tsx';

export interface ReelExportDialogProps {
    open: boolean;
    reel: Reel | null;
    onClose: () => void;
    /** Called after an export has been queued successfully. */
    onQueued?: (result: ReelExportQueued) => void;
}

/**
 * Export popup: same layout as the preview dialog, but only PUBLISHED versions can be selected
 * (drafts cannot be exported). `EXPORT` queues a server-side render of the selected version; the
 * MP4 is written to the user's `Export` folder in the asset library and the user is notified.
 */
export const ReelExportDialog: React.FC<ReelExportDialogProps> = ({ open, reel, onClose, onQueued }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [versions, setVersions] = useState<ReelVersionsResponse | null>(null);
    const [selected, setSelected] = useState<number | null>(null);
    const [snapshot, setSnapshot] = useState<ReelVersionSnapshot | null>(null);
    const [loadingList, setLoadingList] = useState(false);
    const [loadingSnapshot, setLoadingSnapshot] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [queued, setQueued] = useState<ReelExportQueued | null>(null);
    const [exportStatus, setExportStatus] = useState<ReelVersionExportStatus | null>(null);

    const reelId = reel?._id ?? null;

    useEffect(() => {
        if (!open || !reelId) return;
        let cancelled = false;
        setLoadingList(true);
        setError(null);
        setSnapshot(null);
        setVersions(null);
        setQueued(null);
        setExportStatus(null);
        setSelected(null);
        reelMakerApi
            .listReelVersions(reelId)
            .then((data) => {
                if (cancelled) return;
                setVersions(data);
                // Default: latest published version (drafts are not exportable).
                if (data.versions.length > 0) setSelected(data.versions[0].version);
            })
            .catch((err) => {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : String(err);
                Logger.error(LOG_PREFIX, `listReelVersions failed: ${message}`);
                setError(message);
            })
            .finally(() => {
                if (!cancelled) setLoadingList(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, reelId]);

    const loadSnapshot = useCallback(async () => {
        if (!open || !reelId || selected == null) return;
        setLoadingSnapshot(true);
        setError(null);
        try {
            const data = await reelMakerApi.getReelVersion(reelId, selected);
            setSnapshot(data);
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            Logger.error(LOG_PREFIX, `getReelVersion failed: ${message}`);
            setError(message);
        } finally {
            setLoadingSnapshot(false);
        }
    }, [open, reelId, selected]);

    useEffect(() => {
        void loadSnapshot();
    }, [loadSnapshot]);

    const loadExportStatus = useCallback(async () => {
        if (!open || !reelId || selected == null) return;
        try {
            const data = await reelMakerApi.getReelVersionExport(reelId, selected);
            setExportStatus(data);
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            Logger.error(LOG_PREFIX, `getReelVersionExport failed: ${message}`);
        }
    }, [open, reelId, selected]);

    useEffect(() => {
        setExportStatus(null);
        void loadExportStatus();
    }, [loadExportStatus]);

    const exportBusy = exportStatus?.status === 'queued' || exportStatus?.status === 'in-progress';
    useEffect(() => {
        if (!open || !exportBusy) return;
        const id = window.setInterval(() => { void loadExportStatus(); }, 5_000);
        return () => window.clearInterval(id);
    }, [open, exportBusy, loadExportStatus]);

    const options = useMemo(
        () =>
            (versions?.versions ?? []).map((v) => ({
                key: v.version,
                label: v.version === versions?.publishedVersion ? `${v.label} (latest published)` : v.label,
                hint: `Published ${new Date(v.publishedAt).toLocaleString()} · ${v.width} × ${v.height}`,
            })),
        [versions],
    );

    const noPublished = Boolean(versions) && options.length === 0;
    const statusLine = (() => {
        if (!exportStatus || !exportStatus.status || exportStatus.status === 'none') return '';
        if (exportStatus.status === 'queued') return 'Export queued. Waiting for an exporter to pick it up.';
        if (exportStatus.status === 'in-progress') {
            const where = exportStatus.worker ? ` on ${exportStatus.worker}` : '';
            const pct = exportStatus.progress ? ` · ${exportStatus.progress.stage} ${Math.round(exportStatus.progress.percent)}%` : '';
            return `Export in progress${where}${pct}.`;
        }
        if (exportStatus.status === 'completed' && exportStatus.available) return 'Video is ready to download.';
        if (exportStatus.status === 'missing') return 'This version was exported, but the video is no longer in storage.';
        if (exportStatus.status === 'failed') return exportStatus.error || 'The last export of this version failed.';
        return '';
    })();

    const handleExport = async () => {
        if (!reelId || selected == null) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await reelMakerApi.exportReelVersion(reelId, selected);
            setQueued(result);
            onQueued?.(result);
            await loadExportStatus();
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            Logger.error(LOG_PREFIX, `exportReelVersion failed: ${message}`);
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={fullScreen}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1, flexWrap: 'wrap' }}>
                <MovieCreationOutlinedIcon color="secondary" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700 }} noWrap>
                        Export {reel?.name ?? 'reel'} as video
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {snapshot
                            ? `${snapshot.width} × ${snapshot.height} px · MP4 (H.264)${snapshot.publishedAt ? ` · published ${new Date(snapshot.publishedAt).toLocaleString()}` : ''}`
                            : 'Only published versions can be exported'}
                    </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 240 }} disabled={loadingList || options.length === 0}>
                    <InputLabel>Version</InputLabel>
                    <Select
                        label="Version"
                        value={selected == null ? '' : String(selected)}
                        onChange={(e) => setSelected(Number(e.target.value))}
                        renderValue={(value) => options.find((o) => String(o.key) === String(value))?.label ?? String(value)}
                    >
                        {options.map((o) => (
                            <MenuItem key={o.key} value={String(o.key)}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {o.label}
                                        </Typography>
                                        <Chip size="small" label="Published" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {o.hint}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <IconButton size="small" aria-label="Close export" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {error && <Alert severity="error">{error}</Alert>}
                {queued && (
                    <Alert severity="success" icon={<FileDownloadOutlinedIcon fontSize="inherit" />}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {queued.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            {queued.versionLabel} → {queued.location} / {queued.fileName}
                        </Typography>
                    </Alert>
                )}
                {statusLine && (
                    <Alert severity={exportStatus?.status === 'failed' || exportStatus?.status === 'missing' ? 'warning' : exportStatus?.available ? 'success' : 'info'}>
                        {statusLine}
                    </Alert>
                )}
                {noPublished && (
                    <Alert severity="info">
                        This reel has no published version yet. Publish the reel first — drafts cannot be exported.
                    </Alert>
                )}
                {(loadingList || loadingSnapshot) && !snapshot && !noPublished && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                )}
                {snapshot && (
                    <ReelPlayer
                        key={`${snapshot.reelId}:${snapshot.version}:${snapshot.publishedAt ?? ''}`}
                        width={snapshot.width}
                        height={snapshot.height}
                        canvas={snapshot.canvas}
                        autoPlay={false}
                        loop
                        stageHeight={fullScreen ? 'calc(100vh - 280px)' : '56vh'}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    The video is rendered on the server and saved to your <strong>Export</strong> folder in the asset library.
                </Typography>
                <Button onClick={onClose} variant="outlined" color="secondary">
                    {queued ? 'Close' : 'Cancel'}
                </Button>
                {exportStatus?.available && exportStatus.downloadUrl && (
                    <Button
                        component="a"
                        href={exportStatus.downloadUrl}
                        download={exportStatus.fileName || 'reel.mp4'}
                        variant="contained"
                        color="info"
                        startIcon={<FileDownloadOutlinedIcon />}
                    >
                        Download
                    </Button>
                )}
                <Button
                    onClick={handleExport}
                    variant="contained"
                    color="secondary"
                    disabled={submitting || loadingList || selected == null || noPublished || exportBusy}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}
                >
                    {exportBusy ? 'Exporting…' : 'EXPORT'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReelExportDialog;
